/**
 * ProgressAlerts — Flags for coaches: inactive clients, score drops, new PRs.
 *
 * Displayed at the top of the clients tab on the coach dashboard.
 * Zero effect on scoring or matchmaking (doctrine compliant).
 */

import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { useTheme } from '@/contexts/ThemeContext';
import { TYPO } from '@/constants/typography';
import { AlertTriangle, TrendingDown, Trophy, ChevronRight } from 'lucide-react-native';
import type { ClientCardData } from './ClientCard';

export interface AlertItem {
  type: 'inactive' | 'score_drop' | 'new_pr';
  clientId: string;
  clientName: string;
  message: string;
}

interface Props {
  clients: ClientCardData[];
  /** Previous strength scores keyed by client id (for score-drop detection). */
  prevScores?: Map<string, number>;
  /** Client ids that hit a new PR recently. */
  prClientIds?: Set<string>;
  onPressClient: (clientId: string) => void;
}

/**
 * Derive alerts from the current client list.
 */
export function deriveAlerts(
  clients: ClientCardData[],
  prevScores?: Map<string, number>,
  prClientIds?: Set<string>,
): AlertItem[] {
  const alerts: AlertItem[] = [];

  for (const cl of clients) {
    // Inactive 7+ days
    if (cl.last_active) {
      const daysSince = Math.floor(
        (Date.now() - new Date(cl.last_active).getTime()) / 86_400_000,
      );
      if (daysSince >= 7) {
        alerts.push({
          type: 'inactive',
          clientId: cl.id,
          clientName: cl.name,
          message: `${cl.name} hasn't trained in ${daysSince} days`,
        });
      }
    } else {
      alerts.push({
        type: 'inactive',
        clientId: cl.id,
        clientName: cl.name,
        message: `${cl.name} has never logged a workout`,
      });
    }

    // Score drop
    if (prevScores) {
      const prev = prevScores.get(cl.id);
      if (prev !== undefined && cl.strength_score < prev) {
        const diff = prev - cl.strength_score;
        alerts.push({
          type: 'score_drop',
          clientId: cl.id,
          clientName: cl.name,
          message: `${cl.name}'s score dropped ${diff} pts (${prev} → ${cl.strength_score})`,
        });
      }
    }

    // New PR
    if (prClientIds?.has(cl.id)) {
      alerts.push({
        type: 'new_pr',
        clientId: cl.id,
        clientName: cl.name,
        message: `🏆 ${cl.name} hit a new PR!`,
      });
    }
  }

  // Sort: PRs first (celebrate!), then inactive, then score drops
  const ORDER: Record<AlertItem['type'], number> = { new_pr: 0, inactive: 1, score_drop: 2 };
  alerts.sort((a, b) => ORDER[a.type] - ORDER[b.type]);

  return alerts;
}

const ICONS: Record<AlertItem['type'], { Icon: typeof AlertTriangle; color: string }> = {
  inactive: { Icon: AlertTriangle, color: '#eab308' },
  score_drop: { Icon: TrendingDown, color: '#ef4444' },
  new_pr: { Icon: Trophy, color: '#22c55e' },
};

export default function ProgressAlerts({ clients, prevScores, prClientIds, onPressClient }: Props) {
  const { theme } = useTheme();
  const c = theme.colors;

  const alerts = deriveAlerts(clients, prevScores, prClientIds);

  if (alerts.length === 0) return null;

  return (
    <View style={styles.container}>
      <Text style={[styles.title, { color: c.textSecondary, fontFamily: TYPO.heading.semibold }]}>
        ALERTS
      </Text>
      {alerts.slice(0, 5).map((alert, i) => {
        const { Icon, color } = ICONS[alert.type];
        return (
          <TouchableOpacity
            key={`${alert.clientId}-${alert.type}-${i}`}
            style={[styles.alertRow, { backgroundColor: c.surface, borderColor: c.border }]}
            onPress={() => onPressClient(alert.clientId)}
            activeOpacity={0.7}
          >
            <Icon size={16} color={color} />
            <Text style={[styles.alertText, { color: c.text }]} numberOfLines={1}>
              {alert.message}
            </Text>
            <ChevronRight size={14} color={c.textTertiary} />
          </TouchableOpacity>
        );
      })}
      {alerts.length > 5 && (
        <Text style={[styles.moreText, { color: c.textTertiary }]}>
          +{alerts.length - 5} more alert{alerts.length - 5 !== 1 ? 's' : ''}
        </Text>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    gap: 6,
    marginBottom: 12,
  },
  title: {
    fontSize: 12,
    letterSpacing: 1,
    marginBottom: 2,
  },
  alertRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderWidth: 1,
    borderRadius: 2,
  },
  alertText: {
    flex: 1,
    fontSize: 13,
    fontWeight: '600',
  },
  moreText: {
    fontSize: 12,
    fontWeight: '600',
    textAlign: 'center',
    marginTop: 2,
  },
});
